import React, { useCallback, useMemo } from 'react';
import GoogleMapReact, { MapOptions } from 'google-map-react';
import { customMapStyle } from '@/lib/constant';
import classNames from 'classnames';
import {
  CommunicationIcon,
  HomeIcon,
  HospitalIcon,
  MarkProps,
  OfficeIcon,
  ParkIcon,
  SchoolIcon,
  ShoppingDistrictIcon,
} from '@/public/mapMark';
import { LiaHomeSolid } from 'react-icons/lia';
import { TrafficEnvCategory } from '@/swagger';
import Link from 'next/link';
import Flexbox from '@/components/common/Flexbox';
import { BiFullscreen } from 'react-icons/bi';
import FunwooImage from '@/components/common/FunwooImage';
import ReactIcon from '@/src/components/common/ReactIcon';

interface Props extends GoogleMapReact.Props {
  className?: string;
}

export const HomeMarker: React.FC<GoogleMapReact.Coords> = () => (
  <HomeIcon className={'transform -translate-x-1/2 -translate-y-1/2'} />
);
const GoogleMap: React.FC<Props> = ({ className, children, ...props }) => {
  return (
    <div className={classNames(className, 'w-full h-full')}>
      {/*@ts-ignore*/}
      <GoogleMapReact
        options={() =>
          ({
            // draggable: false,
            fullscreenControl: false,
            zoomControl: false,
            styles: customMapStyle,
            panControl: false,
            mapTypeControl: false,
            scrollwheel: false,
          } as MapOptions)
        }
        bootstrapURLKeys={{ key: process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY! }}
        defaultZoom={15}
        {...props}
      >
        {children}
      </GoogleMapReact>
    </div>
  );
};

export default GoogleMap;

const TrafficIcon: Record<TrafficEnvCategory, React.FC<MarkProps>> = {
  [TrafficEnvCategory.COMMUNICATION]: CommunicationIcon,
  [TrafficEnvCategory.SCHOOL]: SchoolIcon,
  [TrafficEnvCategory.SHOPPING_DISTRICT]: ShoppingDistrictIcon,
  [TrafficEnvCategory.HOSPITAL]: HospitalIcon,
  [TrafficEnvCategory.PARK]: ParkIcon,
  [TrafficEnvCategory.OFFICE]: OfficeIcon,
};

export const MarkerIcon: React.FC<
  GoogleMapReact.Coords & {
    id: string;
    selectedCategory: TrafficEnvCategory;
    selectedItemId: string | null;
    setSelectedItemId: (id: string | null) => void;
    setCenter: (center: GoogleMapReact.Coords | null) => void;
  }
> = ({ lng, lat, setCenter, selectedCategory, selectedItemId, setSelectedItemId, id }) => {
  const IconComponent = TrafficIcon[selectedCategory];

  const isSelected = selectedItemId === id;

  const onClick = useCallback<React.MouseEventHandler<SVGAElement>>(
    (event) => {
      const selected = event.currentTarget.dataset.selected === 'true';
      event.preventDefault();
      event.stopPropagation();
      setSelectedItemId(selected ? '' : id);
      setCenter(selected ? null : { lat, lng });
    },
    [id, lng, lat]
  );

  return (
    <IconComponent
      data-selected={isSelected}
      selected={isSelected}
      onClick={onClick}
      className={'transform -translate-x-1/2 -translate-y-1/2'}
    />
  );
};

export const MapCombo: React.FC<{
  className?: string;
  selectedItemId: string | null;
  setSelectedItemId: (id: string | null) => void;
  defaultCenter: GoogleMapReact.Coords;
  center: GoogleMapReact.Coords | null;
  setCenter: (center: GoogleMapReact.Coords | null) => void;
  selectedCategory: TrafficEnvCategory;
  streetViewUrl?: string;
  mapViewUrl?: string;
  items: Array<
    GoogleMapReact.Coords & {
      id: string;
    }
  >;
}> = ({
  className,
  selectedItemId,
  setSelectedItemId,
  defaultCenter,
  center,
  setCenter,
  selectedCategory,
  items,
  streetViewUrl,
  mapViewUrl,
}) => {
  const [boundary, setBoundary] = React.useState<GoogleMapReact.Bounds | null>(null);

  const realCenter = useMemo(() => {
    return center ?? defaultCenter;
  }, [center, defaultCenter]);

  const onMapChange = useCallback<NonNullable<Props['onChange']>>(({ bounds, center }) => {
    setBoundary(bounds);
    setCenter(center);
  }, []);

  const isDefaultCenterInTheView = useMemo(() => {
    if (!boundary) return false;
    const { nw, se } = boundary;

    return (
      nw.lat >= defaultCenter.lat &&
      defaultCenter.lat >= se.lat &&
      nw.lng <= defaultCenter.lng &&
      defaultCenter.lng <= se.lng
    );
  }, [boundary, defaultCenter]);

  const returnCenter = useCallback(() => {
    setSelectedItemId(null);
    setCenter(null);
  }, []);

  return (
    <div className={classNames(className)}>
      <div className={'absolute top-0 left-0 w-full h-full'}>
        <GoogleMap defaultCenter={defaultCenter} center={realCenter} onChange={onMapChange}>
          <HomeMarker {...defaultCenter} />
          {items.map((item) => (
            <MarkerIcon
              key={item.id}
              {...item}
              setSelectedItemId={setSelectedItemId}
              selectedCategory={selectedCategory}
              selectedItemId={selectedItemId}
              setCenter={setCenter}
            />
          ))}
        </GoogleMap>
      </div>
      {!isDefaultCenterInTheView && (
        <button
          type={'button'}
          onClick={returnCenter}
          className={classNames(
            'absolute top-4 right-4',
            'flex items-center',
            'py-2.5 px-4',
            'text-sm2 text-gray700',
            'bg-white'
          )}
        >
          <ReactIcon icon={LiaHomeSolid} className={'mr-2.5 w-4 h-4'} />
          <span>返回建案點</span>
        </button>
      )}
      {mapViewUrl && (
        <Link passHref href={mapViewUrl}>
          <Flexbox
            as={'a'}
            justify={'center'}
            align={'center'}
            className={'absolute top-4 right-4 p-3 text-sm leading-6 bg-white'}
          >
            <span className={'mr-2.5'}>放大</span>
            <ReactIcon icon={BiFullscreen} className={'w-6 h-6'} />
          </Flexbox>
        </Link>
      )}
      {streetViewUrl && (
        <Link passHref href={streetViewUrl}>
          <Flexbox as={'a'} justify={'center'} align={'center'} className={'absolute bottom-4 right-4'}>
            <FunwooImage
              alt={'street view'}
              width={120}
              height={90}
              src={'/street.png'}
              className={'w-[7.5rem] h-[5.625rem]'}
              priority
            />
          </Flexbox>
        </Link>
      )}
    </div>
  );
};
